/**
 * SKIO Discount Integration Module
 * Applies discount pricing and custom badge text to SKIO subscription widget
 *
 * APPROACH:
 * - Listens to SKIO's 'skio::update-selling-plan' event
 * - Waits for Lit's updateComplete promise before modifying shadow DOM
 * - Applies discounts to prices and updates savings badge text
 * - Works with retail-only discount codes (B2B customers excluded)
 *
 * CONFIGURATION:
 * - Enable via theme settings: settings.enable_skio_discounts
 * - Custom badge text: settings.skio_discount_badge_text
 */

if (typeof window.SkioDiscountPrice === 'undefined') {
  window.SkioDiscountPrice = class {
    constructor(discountConfig, customBadgeText) {
      this.discountConfig = discountConfig || {};
      this.customBadgeText = customBadgeText || '';
      this.init();
    }

    init() {
      const discountCode = this.getCookie('discount_code');
      if (!discountCode) return;

      const discount = this.discountConfig[discountCode];
      if (!discount || !this.isDiscountValidForCustomer(discount)) return;

      // Listen to SKIO's update event for proper timing
      document.addEventListener('skio::update-selling-plan', (event) => {
        const picker = event.target;
        if (picker && picker.updateComplete) {
          // Wait for Lit to finish rendering
          picker.updateComplete.then(() => {
            this.applyDiscountToSkio(picker, discount);
          });
        }
      });

      // Listen to theme's variant update event to re-apply discounts
      document.addEventListener('variant:update', () => {
        // Clear all discount flags so we can re-apply
        setTimeout(() => {
          const skioPickers = document.querySelectorAll('skio-plan-picker');
          skioPickers.forEach(picker => {
            if (picker.shadowRoot) {
              picker.shadowRoot.querySelectorAll('[data-discount-applied]').forEach(el => {
                el.removeAttribute('data-discount-applied');
              });
              picker.shadowRoot.querySelectorAll('[data-custom-text-applied]').forEach(el => {
                el.removeAttribute('data-custom-text-applied');
              });
            }

            // Re-apply discounts after SKIO updates
            if (picker.updateComplete) {
              picker.updateComplete.then(() => {
                this.applyDiscountToSkio(picker, discount);
              });
            }
          });
        }, 500); // Give SKIO time to update
      });

      // Also apply on initial load
      this.watchForSkio(discount);
    }

    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    }

    isDiscountValidForCustomer(discount) {
      const isB2B = window.IS_B2B_CUSTOMER || false;
      if (discount.retailOnly && isB2B) return false;
      if (discount.wholesaleOnly && !isB2B) return false;
      return true;
    }

    isDiscountValidForVariant(variantId) {
      const filter = window.VARIANT_FILTER || { mode: 'sitewide', variantIds: [] };

      if (filter.mode === 'sitewide') return true;

      const variantIdStr = String(variantId);
      const isInList = filter.variantIds.includes(variantIdStr);

      if (filter.mode === 'include_only') {
        return isInList;
      } else if (filter.mode === 'exclude') {
        return !isInList;
      }

      return true;
    }

    /**
     * Wait for SKIO to update variant data after variant change
     * Polls SKIO's selectedvariant attribute to ensure it matches target
     */
    waitForSkioVariantUpdate(targetVariantId, discount, retries = 0) {
      const maxRetries = 20;
      const skioPickers = document.querySelectorAll('skio-plan-picker');

      // If no SKIO pickers found, skip polling
      if (skioPickers.length === 0) return;

      // Check if SKIO is actually visible and has subscription options
      let hasVisibleSubscriptions = false;
      skioPickers.forEach(picker => {
        if (picker.shadowRoot) {
          const subscriptionGroups = picker.shadowRoot.querySelectorAll('.group-container');
          // More than 1 group means subscriptions are visible (first is one-time)
          if (subscriptionGroups.length > 1 && picker.offsetParent !== null) {
            hasVisibleSubscriptions = true;
          }
        }
      });

      // If no subscriptions visible, SKIO is in one-time-only mode, skip polling
      if (!hasVisibleSubscriptions) return;

      let allUpdated = true;
      skioPickers.forEach(picker => {
        try {
          const selectedVariantAttr = picker.getAttribute('selectedvariant');
          if (selectedVariantAttr) {
            const variantData = JSON.parse(selectedVariantAttr);
            if (variantData.id !== targetVariantId) {
              allUpdated = false;
            }
          } else {
            allUpdated = false;
          }
        } catch (e) {
          allUpdated = false;
        }
      });

      if (allUpdated) {
        // Variant IDs match - safe to apply discounts
        setTimeout(() => {
          skioPickers.forEach(picker => {
            if (picker.shadowRoot) {
              picker.shadowRoot.querySelectorAll('.skio-price').forEach(el => {
                el.removeAttribute('data-discount-applied');
              });
            }
          });
          this.applyDiscountToSkio(discount);
        }, 200);
      } else if (retries >= maxRetries) {
        // Timed out - SKIO's selectedvariant doesn't match target variant
        // Don't apply discounts to avoid using stale/wrong variant data
        return;
      } else {
        setTimeout(() => this.waitForSkioVariantUpdate(targetVariantId, discount, retries + 1), 100);
      }
    }

    /**
     * Hide SKIO widget (for variants with no subscriptions)
     */
    hideSkioWidget() {
      const skioPickers = document.querySelectorAll('skio-plan-picker');
      skioPickers.forEach(picker => {
        picker.style.display = 'none';
        const container = picker.closest('.skio-plan-picker-container, [data-skio-container]');
        if (container) container.style.display = 'none';
      });
    }

    /**
     * Show SKIO widget (for variants with subscriptions)
     */
    showSkioWidget() {
      const skioPickers = document.querySelectorAll('skio-plan-picker');
      skioPickers.forEach(picker => {
        picker.style.display = '';
        const container = picker.closest('.skio-plan-picker-container, [data-skio-container]');
        if (container) container.style.display = '';
      });
    }

    /**
     * Watch for SKIO widget to load on initial page load
     */
    watchForSkio(discount) {
      const applyToAllPickers = () => {
        const skioPickers = document.querySelectorAll('skio-plan-picker');
        skioPickers.forEach(picker => {
          if (picker.shadowRoot && picker.updateComplete) {
            picker.updateComplete.then(() => {
              this.applyDiscountToSkio(picker, discount);
            });
          }
        });
      };

      // Try immediately
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', applyToAllPickers);
      } else {
        // DOM already loaded, try with small delay for SKIO to initialize
        setTimeout(applyToAllPickers, 500);
      }
    }

    /**
     * Apply discount pricing to SKIO's shadow DOM elements
     * Called after Lit's updateComplete to avoid rendering conflicts
     */
    applyDiscountToSkio(picker, discount) {
      if (!picker || !picker.shadowRoot) return;

      // Check if SKIO is actually showing subscription options
      const subscriptionGroups = picker.shadowRoot.querySelectorAll('.group-container');
      if (subscriptionGroups.length <= 1) return; // Only one-time, skip

      // Get variant data from SKIO's attribute
      let variantData;
      try {
        const selectedVariantAttr = picker.getAttribute('selectedvariant');
        if (selectedVariantAttr) {
          variantData = JSON.parse(selectedVariantAttr);
        }
      } catch (e) {
        return;
      }

      if (!variantData) return;

      // Check if this variant passes the filter
      if (!this.isDiscountValidForVariant(variantData.id)) return;

      const retailPrice = variantData.price;
      const sellingPlanAllocations = variantData.selling_plan_allocations || [];
      const pricesInShadow = picker.shadowRoot.querySelectorAll('.skio-price');

      // Apply discounts to each price element
      pricesInShadow.forEach((priceElement, priceIndex) => {
        // Skip if already processed
        if (priceElement.hasAttribute('data-discount-applied')) return;

        let basePrice, displayPrice;
        const isOnetime = priceIndex === 0;

        if (isOnetime) {
          basePrice = retailPrice;
          if (discount.type === 'percentage') {
            displayPrice = Math.round(basePrice * (100 - discount.value) / 100);
          } else {
            displayPrice = Math.max(0, basePrice - discount.value);
          }
        } else {
          const sellingPlan = sellingPlanAllocations[priceIndex - 1];
          if (!sellingPlan) return;

          basePrice = sellingPlan.price;
          if (discount.type === 'percentage') {
            displayPrice = Math.round(basePrice * (100 - discount.value) / 100);
          } else {
            displayPrice = Math.max(0, basePrice - discount.value);
          }
        }

        if (displayPrice < basePrice) {
          // Update price display with strikethrough
          const formattedBase = this.formatMoney(basePrice);
          const formattedDisplay = this.formatMoney(displayPrice);

          priceElement.innerHTML = `<s style="opacity: 0.6; font-weight: 400;">${formattedBase}</s> ${formattedDisplay}`;
          priceElement.setAttribute('data-discount-applied', 'true');
        }
      });

      // Update savings badge if custom text is provided
      if (this.customBadgeText) {
        this.updateSavingsBadge(picker);
      }
    }

    /**
     * Update the "Save X%" badge with custom text
     */
    updateSavingsBadge(picker) {
      if (!picker.shadowRoot) return;

      const savingsBadges = picker.shadowRoot.querySelectorAll('.savings.bubble');

      savingsBadges.forEach(badge => {
        if (!badge.hasAttribute('data-custom-text-applied')) {
          badge.textContent = this.customBadgeText;
          badge.setAttribute('data-custom-text-applied', 'true');
        }
      });
    }

    /**
     * Format price in cents to currency string
     */
    formatMoney(cents) {
      const dollars = Math.round(cents / 100);

      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
      }

      return `$${dollars}`;
    }
  };

  /**
   * Initialize SKIO discount integration
   * Optionally enabled via theme settings
   */
  window.initSkioDiscount = function(config, customBadgeText) {
    if (window.skioDiscountInitialized) return;
    window.skioDiscountInitialized = true;

    const discountConfig = config || window.DISCOUNT_CONFIG || {};
    const badgeText = customBadgeText || '';

    window.skioDiscountInstance = new window.SkioDiscountPrice(discountConfig, badgeText);
  };
}
